import gsap from 'gsap'
import {ScrollTrigger} from 'gsap/dist/ScrollTrigger'
import React, {useEffect, useMemo, useRef, useState} from 'react'

import IconPlus from '../IconPlus'
import * as styles from './Accordion.module.scss'
import AccordionItem from './AccordionItem'

gsap.registerPlugin(ScrollTrigger)

const normalize = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

const richTextToString = (node) => {
  if (!node) return ''
  if (Array.isArray(node)) return node.map(richTextToString).join(' ')
  if (node.text) return node.text
  return richTextToString(node.content)
}

const Accordion = ({heading, items = [], searchPlaceholder, searchEmptyLabel}) => {
  const [active, setActive] = useState()
  const [query, setQuery] = useState('')
  const headingRef = useRef()
  const itemsRef = useRef([])

  const haystacks = useMemo(
    () =>
      items.map((item) => normalize(`${item.heading || ''} ${richTextToString(item.richText)}`)),
    [items]
  )

  const visibleItems = useMemo(() => {
    const search = normalize(query.trim())
    if (!search) return items
    return items.filter((item, index) => haystacks[index].includes(search))
  }, [items, haystacks, query])

  useEffect(() => {
    const heading = headingRef.current
    const items = itemsRef.current

    if (heading) {
      gsap.from(heading, {
        scrollTrigger: {
          trigger: heading,
          start: 'top 80%',
          end: 'center center',
          once: true,
        },
        yPercent: 60,
        opacity: 0,
      })
    }

    if (items) {
      items.filter(Boolean).forEach((item) => {
        gsap.from(item, {
          scrollTrigger: {
            trigger: item,
            start: 'top 80%',
            end: 'center center',
            once: true,
          },
          yPercent: 60,
          opacity: 0,
        })
      })
    }
  }, [])

  return (
    <section className={styles.section}>
      <h2 className={styles.heading} ref={headingRef}>
        {heading}
      </h2>

      {searchPlaceholder && (
        <div className={styles.search}>
          <input
            type='search'
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              type='button'
              className={styles.searchClear}
              aria-label='Effacer la recherche'
              onClick={() => setQuery('')}
            >
              <IconPlus />
            </button>
          )}
        </div>
      )}

      <div className={styles.items}>
        {visibleItems.map((item, index) => {
          const isActive = index === active
          return (
            <div
              key={item._uid}
              className={styles.item}
              ref={(ref) => {
                itemsRef.current[index] = ref
              }}
            >
              <AccordionItem
                {...item}
                isActive={isActive}
                handleClick={() => setActive((current) => (current === index ? undefined : index))}
              />
            </div>
          )
        })}

        {!visibleItems.length && Boolean(query.trim()) && (
          <p className={styles.empty}>
            {searchEmptyLabel || 'Aucun resultat. Essayez le nom complet de votre banque.'}
          </p>
        )}
      </div>
    </section>
  )
}

export default Accordion
